/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, IIconPackageURLMapping, MediaContext } from "@adapptio/lib-components";
import React, { FunctionComponent } from "react";
import { SvgImage } from "./SvgImage";

/**
 * Icon sizes
 */
export enum ICON_SIZE {
	XXSMALL = "xxsmall", // 12
	XSMALL = "xsmall", // 16
	SMALL = "small", // 20
	MEDIUM = "medium", // 24
	LARGE = "large", // 32
	CUSTOM = "custom"
}

/**
 * Icon props
 */
export interface IIconProps {
	name: string;
	size: ICON_SIZE;
	className?: string;
	showLoader?: boolean;
	/* Overrides url mapping from media context */
	urlMapping?: IIconPackageURLMapping;
	onClick?: React.MouseEventHandler<HTMLSpanElement>;
}

/**
 * Icon component
 */
const Icon: FunctionComponent<IIconProps> = ({ name, size, className, showLoader, urlMapping, onClick }) => {
	const [ packageName, iconName ] = (name || "/").split("/");
	const mediaContext = React.useContext(MediaContext);

	if (!packageName || !iconName || !mediaContext) {
		return null;
	}

	const prefixUrl = urlMapping ? urlMapping[packageName] : mediaContext.iconPackageUrlMapping[packageName];
	const path = `${prefixUrl}/${iconName}.svg`;

	const baseClass = BEM("edt-icon");
	baseClass.mod("size-" + size);

	if (className) {
		baseClass.add(className);
	}

	return (
		<span className={baseClass.toClassName()} onClick={onClick}>
			<SvgImage source={path} showLoader={showLoader} cacheRequests={true} />
		</span>
	);
};

const IconMemo = React.memo(Icon);

export { IconMemo as Icon };
